import React, { useState, useEffect, useRef } from 'react';
import get from 'lodash/get';
import axios from 'axios';

import { setLoading, showErrorModal, useApplicationState } from '../../utilities/applicationState';
import '../common/newListTable.scss';
import './viewPartnerProjects.scss';
import { SdfBusyIndicator, SdfButton, SdfFocusPane, SdfGrid, SdfIcon, SdfLink, SdfQuickStat, SdfSelect, SdfxPagination } from '@synerg/react-components';
import { AgGridReact } from 'ag-grid-react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import TooltipComponent from '../common/tooltipComponent';
import { PAGE_SIZES } from '../constants';

const AVAILABLE_PAGE_SIZES = [];

export default function ViewPartnerProjects({ partnerName, clientId, ooid, totalProjects, setViewProjects, setShowView, setPreSelectedTab }) {
	const [, dispatch] = useApplicationState();
	const [onChangePageSize, setOnChangePageSize] = useState(10);
	const [onChangePage, setOnChangePage] = useState(1);
	const grid = useRef();
	const [dataLoading, setDataLoading] = useState(false);
	const [cancelableRequest, setCancelableRequest] = useState(false);
	const [tableData, setTableData] = useState([]);
	const [sortOrder, setSortOrder] = useState({});
	const [start, setStart] = useState(1);
	const [end, setEnd] = useState(10);

	// pagination
	const [selectedPaginationOptions, setSelectedPaginationOptions] = useState({
		totalPages: 1,
		pageSize: 10,
		page: 1,
		skip: 0
	});

	const ProjectInfo = (content) => {
		return (
			<TooltipComponent content={content} direction="right">
				<span className="truncate" style={{ width: '150px', display: 'block' }}>
					{content}
				</span>
			</TooltipComponent>
		)
	};

	const customStringComparator = (valueA, valueB) => {
		return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
	};

	const defaultColDef = {
		sortable: true,
		filter: false,
		resizable: false,
		suppressMovable: true,
		suppressMenu: true
	};

	const gridOptions = {
		defaultColDef: defaultColDef,
		pagination: false,
		suppressPaginationPanel: true,
		paginationPageSize: 10,
		suppressHorizontalScroll: true
	};

	const getSearchParams = () => {
		const params = {
			ooid,
			skip: selectedPaginationOptions.skip,
			pageSize: selectedPaginationOptions.pageSize,
			orderColumn: sortOrder.colId,
			order: sortOrder.sortBy,
		};
		return Object.keys(params).reduce((prev, key) => {
			if (params[key] || params[key] === 0) {
				prev[key] = params[key];
			}
			return prev;
		}, {});
	};

	const updateStartAndEnd = (total, pageNo, pageSize) => {
		setStart(total > 0 ? (pageNo - 1) * pageSize + 1 : 0);
		setEnd((pageNo * pageSize) > total ? total : (pageNo * pageSize));
	};

	const getDataForPartnerProjects = async () => {
		try {
			dispatch(setLoading(true));
			setDataLoading(true);
			const { data } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/partner/total-projects`, {
				cancelToken: getCancelableRequestToken().token,
				params: getSearchParams()
			});
			setTableData(data);
			setSelectedPaginationOptions({
				totalPages: data.totalPages,
				skipped: data.options.skip,
				pageSize: data.options.pageSize,
				page: Math.ceil(data.options.skip / data.options.pageSize + 1) || 1
			});
			updateStartAndEnd(data.total, Math.ceil(parseInt(data.options.skip) / parseInt(data.options.pageSize)) + 1, parseInt(data.options.pageSize));
			dispatch(setLoading(false));
			setDataLoading(false);
		} catch (e) {
			if (e && e.message !== 'search request canceled') {
				const errorMessage = get(e, 'response.data.message', 'Something went wrong when retrieving list of partner projects. Please try again.');
				dispatch(showErrorModal(errorMessage));
				dispatch(setLoading(false));
				setDataLoading(false);
			}
		}
	};

	const myIcons = {
		first: "<sdf-icon icon='nav-page-first' />",
		previous: "<sdf-icon icon='nav-page-back' />",
		next: "<sdf-icon icon='nav-page-next' />",
		last: "<sdf-icon icon='nav-page-last' />",
		sortAscending: "<sdf-icon icon='action-sort-asc' />",
		sortDescending: "<sdf-icon icon='action-sort-desc' />",
		sortUnSort: "<sdf-icon icon='action-sort' />"
	};

	const getCancelableRequestToken = () => {
		if (cancelableRequest) {
			cancelableRequest.cancel('search request canceled');
		}
		const newRequest = axios.CancelToken.source();
		setCancelableRequest(newRequest);
		return newRequest;
	};

	useEffect(() => {
		AVAILABLE_PAGE_SIZES.length = 0;
		if (tableData.total) {
			PAGE_SIZES.every((element) => {
				if (tableData.total > element.value) {
					AVAILABLE_PAGE_SIZES.push(element);
					return true;
				}
				else {
					AVAILABLE_PAGE_SIZES.push(element);
					return false;
				}
			})
		}
	}, [tableData]);

	useEffect(() => {
		if (sortOrder || onChangePageSize || onChangePage || ooid) {
			getDataForPartnerProjects();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ooid, onChangePageSize, onChangePage, sortOrder]);

	const onManageMembersClicked = (props) => {
		setViewProjects(false);
		setShowView(true);
		setPreSelectedTab('Members')
	}
	const ViewLink = (props) => {
		const history = useHistory();
		return (
			<SdfButton size="sm" emphasis={"tertiary"} className={"whitespace-nowrap"}
				onClick={() => {
					history.push({ pathname: `/partner-projects/${props.data.projectAppId}`, state: props.data })
				}}
			>
				View
			</SdfButton>
		);
	};

	const columnDefs = [
		{
			field: "projectName",
			headerName: "Project name",
			headerClass: "bg-action-default-100 text-heading-01",
			sortable: true,
			suppressMovable: true,
			cellRenderer: (params) => ProjectInfo(params.data.projectName),
			cellStyle: { overflow: 'visible', textOverflow: 'ellipsis' },
			comparator: customStringComparator,
			flex: 1,
			minWidth: 150
		},
		{
			field: "sor",
			headerName: "SOR",
			sortable: true,
			suppressMovable: true,
			headerClass: "bg-action-default-100 text-heading-01",
			width: 130,
			flex:1
		},
		{
			field: "projectType",
			headerName: "Environment",
			sortable: true,
			suppressMovable: true,
			headerClass: "bg-action-default-100 text-heading-01",
			flex:1
		},
		{
			field: "legacy",
			headerName: "Legacy",
			sortable: true,
			suppressMovable: true,
			headerClass: "bg-action-default-100 text-heading-01",
			width: 120,
			flex:1
		},
		{
			field: "locale",
			headerName: "Locale",
			sortable: true,
			suppressMovable: true,
			headerClass: "bg-action-default-100 text-heading-01",
			width: 120,
			flex:1
		},
		{
			field: "view",
			headerName: "",
			sortable: false,
			suppressMovable: true,
			cellRenderer: (event) => ViewLink(event),
			suppressColumnsToolPanel: true,
			headerClass: "bg-action-default-100 text-heading-01",
			width: 120,
			flex:1
			
		}
	];

	return (

		<SdfFocusPane visible={true}
			paneType="anchored"
			size="lg"
			hideAcceptButton={true}
			hideDismissButton={true}
			onSdfDismiss={() => { setViewProjects(false); }}
			closeable={true}
			dismissOnClickOutside={true}
			hideFooter={true}
		>
			{dataLoading && <SdfBusyIndicator
				style={{ color: "#1a8099" }}
				size="xl"
				fullScreen={true}
			></SdfBusyIndicator>
			}
			<div className="px-5">
				<div className={"flex mx-0 justify-between items-center"}>
					<div className={"block w-lg text-heading-05 font-bold"}>{partnerName}</div>
					<SdfLink href='#' onClick={onManageMembersClicked}><SdfIcon icon="external-link" /> Manage members</SdfLink>
				</div>
				<div className="mini-table mt-5 mb-5">
					<div className="flex flex-row gap-20 mt-4">
						<SdfQuickStat
							label="Partner ID"
							size="sm"
							labelPosition="bottom"
						>{clientId || 'N/A'}</SdfQuickStat>
						<SdfQuickStat
							label="Partner OOID"
							labelPosition="bottom"
							size="sm"
						>{ooid || 'N/A'}</SdfQuickStat>
						<SdfQuickStat
							label="Total projects"
							labelPosition="bottom"
							size="sm"
						>{totalProjects || tableData.total || 0}</SdfQuickStat>
					</div>
				</div>
				<div className="text-heading-03 font-bold mt-8 mb-2">
					Project details
				</div>
				<SdfGrid className={"ag-theme-alpine"}>
					<AgGridReact
						rowHeight={50}
						ref={grid}
						headerHeight={50}
						domLayout='autoHeight'
						gridOptions={gridOptions}
						icons={myIcons}
						rowData={tableData.list || []}
						rowClass={"template-row pt-1 cursor-pointer"}
						autoSizeAllColumns={true}
						unSortIcon={true}
						columnDefs={columnDefs}
						onGridReady={(params) => params.api.sizeColumnsToFit()}
						onSortChanged={($event) => {
							try {
								const cols = get(grid, 'current.columnApi.columnModel.gridColumns', []);
								const sortObj = cols.map((elem) => {
									if (elem.hasOwnProperty('sort') && elem.hasOwnProperty('colId')) {
										if (elem.sort !== undefined) {
											return { colId: elem.colId, sortBy: elem.sort }
										}
									}
									return null;
								}).filter(elem => elem !== null);
								if (sortObj.length > 0) {
									if (sortObj[0].sortBy !== null) {
										setSelectedPaginationOptions((prev) => {
											return { ...prev, skip: 0 }
										})
										setSortOrder({ colId: sortObj[0].colId, sortBy: sortObj[0].sortBy });
									}
								}
							} catch (e) { }
						}}
					>

					</AgGridReact>
				</SdfGrid>

				<div className="flex items-center justify-end pt-4 pb-20">
					<div className="text-sm text-action-default">Rows per page:</div>
					<SdfSelect
						className="w-20 text-sm text-action-default px-4 mb-1 border-r-1 border-t-0 border-l-0 border-b-0 border-dark border-solid"
						label ="rows per page"
                        hideLabel= {true}
						items={AVAILABLE_PAGE_SIZES}
						onSdfChange={({ detail }) => {
							setSelectedPaginationOptions((prev) => {
								return { ...prev, page: 1, skip: 0, pageSize: detail.value }
							});
							setOnChangePageSize(detail.value);
						}}
						value={{ label: `${selectedPaginationOptions.pageSize}`, value: selectedPaginationOptions.pageSize }}
					/>
					<div className="text-sm text-action-default px-4">{`${start} - ${end} items`}</div>
					{selectedPaginationOptions.totalPages >= 1 &&
						<SdfxPagination
							currentPage={selectedPaginationOptions.page || 1}
							totalPages={selectedPaginationOptions.totalPages || 0}
							onSdfxPageChange={({ detail }) => {
								setSelectedPaginationOptions((prev) => {
									return { ...prev, page: detail, skip: (detail - 1) * prev.pageSize }
								})
								setOnChangePage(detail);
							}}
						/>
					}
				</div>
			</div>
		</SdfFocusPane>
	);
}
